
import { useCallback } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useSupabase } from "@/hooks/useSupabase";
import { supabase } from "@/integrations/supabase/client";
import { isMockArticleId } from '@/utils/articleIdValidation';

export const useArticleComment = (articleId: string) => {
  const { user } = useSupabase();
  const { toast } = useToast();

  const handleComment = useCallback(async (content: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to comment",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      // For mock articles (numeric IDs), we'll use localStorage
      if (isMockArticleId(articleId)) {
        const newMockComment = {
          id: crypto.randomUUID(),
          content: content,
          created_at: new Date().toISOString(),
          user_id: user.id,
          user_name: user.user_metadata?.full_name || 'Anonymous'
        };
        
        const storedMockComments = localStorage.getItem(`mockComments_${articleId}`);
        const existingComments = storedMockComments ? JSON.parse(storedMockComments) : [];
        const updatedComments = [newMockComment, ...existingComments];
        
        localStorage.setItem(`mockComments_${articleId}`, JSON.stringify(updatedComments));
        
        toast({
          title: "Success",
          description: "Comment added successfully"
        });
        return true;
      }

      const { error } = await supabase
        .from('article_comments')
        .insert({ 
          article_id: articleId,
          user_id: user.id,
          content
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Comment added successfully"
      });
      return true;
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive"
      });
      return false;
    }
  }, [articleId, user, toast]);

  return { handleComment };
};
